import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Skeleton } from "@/components/ui/skeleton";
import {
  UserPlus,
  FlaskConical,
  CreditCard,
  TrendingUp,
  Repeat,
  UserMinus,
  Coins,
  Wallet,
  Target,
} from "lucide-react";

interface SaasOverview {
  signups: number;
  trialsActive: number;
  metrics: {
    mrr: number;
    activeSubs: number;
    trialingSubs: number;
    churned30d: number;
    arpu: number;
  };
  revenueInRange: number;
  ltv: number;
}

interface Props {
  datePreset: string;
  customRange?: { since: string; until: string };
  spend: number; // wydatki na reklamy w okresie — do ROAS
  currency?: string;
}

/** Mapuje preset zakresu (zgodny z dashboardem) na from/to ISO dla endpointu. */
function presetToRange(
  datePreset: string,
  customRange?: { since: string; until: string }
): { from: string; to: string } {
  const now = new Date();
  const start = new Date(now);
  switch (datePreset) {
    case "today":
      start.setHours(0, 0, 0, 0);
      break;
    case "yesterday":
      start.setDate(now.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      break;
    case "last_14d":
      start.setDate(now.getDate() - 14);
      break;
    case "last_30d":
      start.setDate(now.getDate() - 30);
      break;
    case "last_90d":
      start.setDate(now.getDate() - 90);
      break;
    case "this_month":
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      break;
    case "last_month": {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
      return { from: s.toISOString(), to: e.toISOString() };
    }
    case "custom":
      if (customRange) {
        return {
          from: new Date(customRange.since).toISOString(),
          to: new Date(`${customRange.until}T23:59:59`).toISOString(),
        };
      }
      start.setDate(now.getDate() - 7);
      break;
    case "last_7d":
    default:
      start.setDate(now.getDate() - 7);
      break;
  }
  return { from: start.toISOString(), to: now.toISOString() };
}

function fmtMoney(value: number, currency = "PLN"): string {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

export default function SaasFunnelCards({
  datePreset,
  customRange,
  spend,
  currency = "PLN",
}: Props) {
  const { from, to } = presetToRange(datePreset, customRange);
  const { data, isLoading, isError } = useQuery<SaasOverview>({
    queryKey: ["/api/dashboard/saas-overview", from, to],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/dashboard/saas-overview?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
      ),
    staleTime: 60_000,
  });

  if (isError) return null;

  const m = data?.metrics;
  const roas = data && spend > 0 ? data.revenueInRange / spend : null;

  const cards: {
    label: string;
    value: string;
    sub?: string;
    Icon: typeof UserPlus;
    accent?: boolean;
  }[] = [
    { label: "Konta założone", value: data ? String(data.signups) : "—", Icon: UserPlus },
    { label: "Aktywne triale", value: data ? String(data.trialsActive) : "—", Icon: FlaskConical },
    {
      label: "Aktywne subskrypcje",
      value: m ? String(m.activeSubs) : "—",
      sub: m ? `${m.trialingSubs} w trialu` : undefined,
      Icon: CreditCard,
    },
    { label: "MRR", value: m ? fmtMoney(m.mrr, currency) : "—", Icon: TrendingUp, accent: true },
    { label: "ARPU", value: m ? fmtMoney(m.arpu, currency) : "—", Icon: Coins },
    { label: "Churn (30 dni)", value: m ? String(m.churned30d) : "—", Icon: UserMinus },
    { label: "LTV", value: data ? fmtMoney(data.ltv, currency) : "—", Icon: Repeat },
    {
      label: "Przychód (okres)",
      value: data ? fmtMoney(data.revenueInRange, currency) : "—",
      Icon: Wallet,
    },
    {
      label: "ROAS",
      value: roas != null ? `${roas.toFixed(2)}x` : "—",
      sub: spend > 0 ? `${fmtMoney(spend, currency)} ads` : "brak wydatków",
      Icon: Target,
      accent: true,
    },
  ];

  return (
    <div className="mb-6">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Lejek SaaS
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {/* VP-208: w trakcie ładowania kafelki pokazywały przyciemnione zera
            (`opacity-60`) — zamiast tego placeholder w kształcie realnej karty. */}
        {isLoading
          ? Array.from({ length: cards.length }).map((_, i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-4">
                <div className="mb-2 flex items-center justify-between">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-4 w-4 rounded shrink-0" />
                </div>
                <Skeleton className="h-6 w-20" />
                <Skeleton className="mt-1.5 h-3 w-14" />
              </div>
            ))
          : cards.map((c) => (
              <div
                key={c.label}
                className={`rounded-xl border p-4 ${c.accent ? "border-[#ef3e29]/40 bg-[#ef3e29]/[0.06]" : "border-border bg-card"}`}
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{c.label}</span>
                  <c.Icon
                    className={`h-4 w-4 ${c.accent ? "text-[#ef3e29]" : "text-muted-foreground/60"}`}
                  />
                </div>
                <div className="text-xl font-extrabold text-foreground">{c.value}</div>
                {c.sub && <div className="mt-0.5 text-xs text-muted-foreground">{c.sub}</div>}
              </div>
            ))}
      </div>
    </div>
  );
}
